import type { CSSProperties, ReactNode } from 'react';
import { useTheme } from '../../theme';

export interface AdminDashboardChartCardProps {
  title: string;
  subtitle: string;
  /** Ícone opcional (ex. SVG) no canto superior direito */
  headerIcon?: ReactNode;
  children: ReactNode;
  /** Aria do cartão para leitores de ecrã */
  'aria-label'?: string;
}

export function AdminDashboardChartCard({
  title,
  subtitle,
  headerIcon,
  children,
  'aria-label': ariaLabel,
}: AdminDashboardChartCardProps) {
  const theme = useTheme();
  const c = theme.palette.adminDashboard.charts;

  const cardStyle: CSSProperties = {
    boxSizing: 'border-box',
    minHeight: c.cardMinHeight,
    background: c.cardBackground,
    border: `${theme.shape.borderWidth.thin} solid ${c.cardBorder}`,
    borderRadius: c.cardBorderRadius,
    padding: c.cardPadding,
    boxShadow: c.cardShadow,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm2,
    minWidth: 0,
  };

  return (
    <section
      style={cardStyle}
      aria-label={ariaLabel ?? title}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: theme.spacing.sm,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              fontSize: theme.typography.fontSize.chartCardTitle,
              fontWeight: theme.typography.fontWeight.semibold,
              color: c.titleColor,
              lineHeight: theme.typography.lineHeight.tight,
            }}
          >
            {title}
          </h2>
          <p
            style={{
              margin: `${theme.spacing.xs} 0 0`,
              fontSize: theme.typography.fontSize.chartCardSubtitle,
              fontWeight: theme.typography.fontWeight.normal,
              color: c.subtitleColor,
              lineHeight: theme.typography.lineHeight.normal,
            }}
          >
            {subtitle}
          </p>
        </div>
        {headerIcon ? (
          <div
            style={{
              flexShrink: 0,
              color: c.iconAccent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden
          >
            {headerIcon}
          </div>
        ) : null}
      </div>
      <div style={{ flex: 1, minHeight: 0, width: '100%' }}>{children}</div>
    </section>
  );
}
